import { Outlet, NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { DataProvider, useData } from "../../context/DataContext";
import { fmtDate, fmtMoney } from "../../lib/format";
import PageSkeleton from "../../components/PageSkeleton";
import { LogOut } from "lucide-react";

function PortfolioHeader() {
  const { user, logout } = useAuth();
  const { data } = useData();
  const navigate = useNavigate();

  const isAssociate = user?.role === "associate";
  const isLeaveEditor = user?.role === "leave_editor";
  const navItems = isLeaveEditor
    ? [
        { to: "/portfolio/projects", label: "Projects" },
        { to: "/portfolio/leave",    label: "Leave" },
        { to: "/portfolio/help",     label: "Manual" },
      ]
    : isAssociate
    ? [
        { to: "/portfolio/projects", label: "Portfolio" },
        { to: "/portfolio/summary",  label: "Summary" },
        { to: "/portfolio/add",      label: "New Project" },
        { to: "/portfolio/help",     label: "Manual" },
      ]
    : [
        { to: "/portfolio/projects", label: "My Portfolio" },
        { to: "/portfolio/add",      label: "Add Project" },
        { to: "/portfolio/summary",  label: "My Summary" },
        { to: "/portfolio/capacity", label: "Capacity" },
        { to: "/portfolio/leave",    label: "Leave" },
        { to: "/portfolio/help",     label: "Manual" },
      ];

  return (
    <header className="border-b border-[var(--rule-strong)] bg-white">
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between gap-4 pt-5 pb-4">
          <div className="min-w-0">
            <div className="eyebrow mb-1">{user?.role === "associate" ? "Associate workspace" : user?.role === "leave_editor" ? "Firm-wide projects · leave" : "Principal portfolio"}</div>
            <h1 className="font-display tracked text-xl md:text-2xl text-[var(--ink)] whitespace-nowrap">
              MONK MACKENZIE
            </h1>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <div className="text-right">
              <div className="font-mono-num text-sm text-[var(--ink)]" data-testid="user-name">{user?.name}</div>
              <div className="eyebrow text-[var(--soft-fg)] normal-case tracking-[0.06em]">
                {fmtDate(data?.meta?.reportDate)}
              </div>
            </div>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              title="Sign out"
              className="p-2 border border-[var(--rule-strong)] hover:bg-[var(--paper-soft)]"
              data-testid="logout-button"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
        <nav className="flex gap-6 lg:gap-9 overflow-x-auto no-scrollbar whitespace-nowrap" data-testid="portfolio-nav">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}

export default function PortfolioShell() {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "principal" && user.role !== "associate" && user.role !== "leave_editor") return <Navigate to="/admin/overview" replace />;
  return (
    <DataProvider>
      <div className="min-h-screen bg-white">
        <PortfolioHeader />
        <main className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-10 py-8">
          <PortfolioMain />
        </main>
      </div>
    </DataProvider>
  );
}

function PortfolioMain() {
  const { data, loading, error, refresh } = useData();
  const { pathname } = useLocation();
  if (loading && !data) return <div data-testid="data-loading"><PageSkeleton /></div>;
  if (error) {
    return (
      <div className="py-24 text-center" data-testid="data-error">
        <div className="eyebrow text-[#6B2F2A] mb-2">Could not load your portfolio</div>
        <div className="font-mono-num text-sm text-[#6B2F2A]">{String(error)}</div>
        <button
          onClick={refresh}
          className="mt-6 px-4 py-2 border border-[var(--rule-strong)] text-xs font-display tracked-tight uppercase hover:bg-[var(--paper-soft)]"
          data-testid="data-error-retry"
        >
          Try again
        </button>
      </div>
    );
  }
  return (
    <div className="animate-page">
      {!pathname.startsWith("/portfolio/help") && <WelcomeBanner />}
      <Outlet />
    </div>
  );
}

function WelcomeBanner() {
  const { data } = useData();
  const { user } = useAuth();
  const projectCount = data?.projects?.length ?? 0;
  if (!data) return null;
  const isLeaveEditor = user?.role === "leave_editor";
  return (
    <div className="mb-8 p-4 border border-[var(--rule-strong)] bg-[var(--paper-soft)]" data-testid="welcome-banner">
      <div className="eyebrow text-[var(--soft-fg)]">{isLeaveEditor ? "Firm-wide" : "This month"}</div>
      <div className="font-display text-sm mt-1">
        {isLeaveEditor
          ? <>Review or edit any of the firm's <span className="font-mono-num text-[var(--ink)]">{projectCount}</span> {projectCount === 1 ? "project" : "projects"} — grouped by principal below.</>
          : <>Review and update your <span className="font-mono-num text-[var(--ink)]">{projectCount}</span> {projectCount === 1 ? "project" : "projects"} for the forecast period.</>}
      </div>
    </div>
  );
}
